import { ApiError } from 'api/core/ApiError';

const DROPBOX_AUTH_ERROR_TAGS = [
  'invalid_access_token',
  'expired_access_token',
  'invalid_grant',
  'revoked',
  'dropbox is not connected',
  'not_connected',
];

const getAxiosResponseData = (error: Error): unknown => {
  const {response} = (error as Record<string, unknown>);
  if (response && typeof response === 'object') {
    return (response as Record<string, unknown>).data;
  }
  return undefined;
};

const getErrorPayloadText = (error: unknown): string => {
  if (error instanceof ApiError) {
    try {
      return JSON.stringify(error.body ?? {}).toLowerCase();
    } catch {
      return String(error.body ?? '').toLowerCase();
    }
  }
  if (error instanceof Error) {
    const responseData = getAxiosResponseData(error);
    if (responseData !== undefined) {
      try {
        return JSON.stringify(responseData).toLowerCase();
      } catch {
        return String(responseData).toLowerCase();
      }
    }
    return error.message.toLowerCase();
  }
  try {
    return JSON.stringify(error).toLowerCase();
  } catch {
    return String(error).toLowerCase();
  }
};

const getAxiosResponseStatus = (error: unknown): number | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const {response} = (error as Record<string, unknown>);
  if (response && typeof response === 'object') {
    const {status} = (response as Record<string, unknown>);
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
};

export const isDropboxAuthError = (error: unknown): boolean => {
  if (error instanceof ApiError && error.status === 401) {
    return true;
  }
  if (getAxiosResponseStatus(error) === 401) {
    return true;
  }
  const errorText = getErrorPayloadText(error);
  return DROPBOX_AUTH_ERROR_TAGS.some(tag => errorText.includes(tag));
};

export const isDropboxPathNotFoundError = (error: unknown): boolean => {
  const errorText = getErrorPayloadText(error);
  return errorText.includes('path/not_found') || errorText.includes('"not_found"');
};

export const isDropboxPathConflictError = (error: unknown): boolean => {
  const errorText = getErrorPayloadText(error);
  return errorText.includes('path/conflict');
};

/**
 * Returns true when ensureAuthenticated already triggered an OAuth redirect
 * (i.e. startOAuth was called and is in-flight). In this case the caller
 * should reset UI to idle but must NOT call connectDropbox() again — doing
 * so would overwrite the in-flight OAuth state in localStorage and break the
 * callback verification.
 */
export const isDropboxReconnecting = (error: unknown): boolean =>
  error instanceof Error && error.message === 'Dropbox authentication required';
